import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; 

// A custom hook to detect screen size using media queries
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    // Use the modern 'change' event listener
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};


const AIWorkshopLanding1 = () => {
  // Hook to check if the screen is mobile-sized (<= 768px)
  const isMobile = useMediaQuery('(max-width: 768px)');

  //================================================================================
  //== STYLES
  //================================================================================

  const mainStyles = {
    appContainer: {
      background: 'linear-gradient(135deg, #0a0a2e 0%, #16213e 30%, #0f3460 70%, #1a0b2e 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      color: 'white',
      position: 'relative',
      overflowX: 'hidden'
    },
    backgroundOverlay: {
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: `radial-gradient(circle at 20% 80%, rgba(255, 20, 147, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.4) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(30, 144, 255, 0.3) 0%, transparent 50%)`,
      pointerEvents: 'none'
    },
    networkMesh: {
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      backgroundImage: `linear-gradient(rgba(255, 20, 147, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 20, 147, 0.1) 1px, transparent 1px)`,
      backgroundSize: '100px 100px',
      pointerEvents: 'none',
      opacity: 0.3
    },
    sectionWrapper: {
      minHeight: '100vh',
      padding: isMobile ? '60px 15px' : '80px 20px', // Adjusted padding for mobile
      position: 'relative',
      zIndex: 2,
    },
    contactSectionWrapper: {
        minHeight: '100vh',
        padding: isMobile ? '60px 15px' : '80px 20px', // Adjusted padding for mobile
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }
  };

  const workshopSectionStyles = {
    contentWrapper: { maxWidth: '1200px', margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: '60px' },
    badge: { backgroundColor: 'rgba(79, 124, 255, 0.15)', border: '1px solid rgba(79, 124, 255, 0.4)', borderRadius: '25px', padding: '8px 20px', fontSize: '0.9rem', fontWeight: 500, marginBottom: '24px', backdropFilter: 'blur(15px)', color: '#87ceeb', display: 'inline-block' },
    mainTitle: { fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '16px', color: 'white' },
    subtitle: { fontSize: isMobile ? '1.05rem' : '1.2rem', color: 'rgba(255, 255, 255, 0.8)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 },
    workshopsGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px', marginBottom: '40px' },
    workshopCard: { backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '20px', padding: isMobile ? '24px' : '32px', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' },
    cardGlow: { position: 'absolute', top: 0, left: 0, right: 0, height: '2px', borderRadius: '20px 20px 0 0' },
    beginnerGlow: { background: 'linear-gradient(90deg, #4f7cff, #3d63cc)' },
    intermediateGlow: { background: 'linear-gradient(90deg, #22c55e, #16a34a)' },
    allLevelsGlow: { background: 'linear-gradient(90deg, #f59e0b, #d97706)' },
    advancedGlow: { background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)' },
    executiveGlow: { background: 'linear-gradient(90deg, #ef4444, #dc2626)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
    levelBadge: { padding: '6px 16px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
    beginnerBadge: { backgroundColor: 'rgba(79, 124, 255, 0.2)', color: '#4f7cff', border: '1px solid rgba(79, 124, 255, 0.3)' },
    intermediateBadge: { backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)' },
    allLevelsBadge: { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.3)' },
    advancedBadge: { backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6', border: '1px solid rgba(139, 92, 246, 0.3)' },
    executiveBadge: { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' },
    price: { fontSize: '2rem', fontWeight: 800, color: 'white' },
    cardTitle: { fontSize: '1.4rem', fontWeight: 700, color: 'white', marginBottom: '12px', lineHeight: 1.3 },
    cardDescription: { fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, marginBottom: '20px' },
    cardMeta: { display: 'flex', gap: '20px', marginBottom: '20px', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', flexWrap: 'wrap' },
    metaItem: { display: 'flex', alignItems: 'center', gap: '6px' },
    topicsContainer: { marginBottom: '24px' },
    topicsTitle: { fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.8)', marginBottom: '12px' },
    topicsList: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    topicChip: { backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '15px', padding: '6px 12px', fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)' },
    enrollBtn: { width: '100%', padding: '14px 24px', borderRadius: '12px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', border: 'none' },
    beginnerBtn: { background: 'linear-gradient(135deg, #4f7cff 0%, #3d63cc 100%)', color: 'white', boxShadow: '0 4px 20px rgba(79, 124, 255, 0.3)' },
    intermediateBtn: { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', boxShadow: '0 4px 20px rgba(34, 197, 94, 0.3)' },
    allLevelsBtn: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)' },
    advancedBtn: { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)' },
    executiveBtn: { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)' },
    bottomRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }
  };
  
  const bootcampSectionStyles = {
    contentWrapper: { maxWidth: '1400px', margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: '60px' },
    badge: { backgroundColor: 'rgba(79, 124, 255, 0.15)', border: '1px solid rgba(79, 124, 255, 0.4)', borderRadius: '25px', padding: '8px 20px', fontSize: '0.9rem', fontWeight: 500, marginBottom: '24px', backdropFilter: 'blur(15px)', color: '#87ceeb', display: 'inline-block' },
    mainTitle: { fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '16px', color: 'white' },
    subtitle: { fontSize: isMobile ? '1.05rem' : '1.2rem', color: 'rgba(255, 255, 255, 0.8)', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6 },
    bootcampsGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(380px, 1fr))', gap: '30px' },
    bootcampCard: { backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '20px', padding: isMobile ? '24px' : '32px', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' },
    cardGlow: { position: 'absolute', top: 0, left: 0, right: 0, height: '3px', borderRadius: '20px 20px 0 0' },
    aiEngineerGlow: { background: 'linear-gradient(90deg, #4f7cff, #3d63cc)' },
    dataScienceGlow: { background: 'linear-gradient(90deg, #22c55e, #16a34a)' },
    leadershipGlow: { background: 'linear-gradient(90deg, #f59e0b, #d97706)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
    cardIcon: { width: '60px', height: '60px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px' },
    aiEngineerIcon: { backgroundColor: 'rgba(79, 124, 255, 0.2)', color: '#4f7cff' },
    dataScienceIcon: { backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' },
    leadershipIcon: { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' },
    popularBadge: { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '6px 12px', borderRadius: '15px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' },
    cardTitle: { fontSize: '1.5rem', fontWeight: 700, color: 'white', marginBottom: '8px', lineHeight: 1.3 },
    cardSubtitle: { fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '16px' },
    cardDescription: { fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6, marginBottom: '24px' },
    cardMeta: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px', fontSize: '0.9rem' },
    metaItem: { display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255, 255, 255, 0.7)' },
    metaIcon: { fontSize: '16px' },
    includesSection: { marginBottom: '24px' },
    includesTitle: { fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' },
    includesList: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' },
    includeItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' },
    checkIcon: { color: '#22c55e', fontSize: '14px' },
    outcomesSection: { marginBottom: '24px' },
    outcomesTitle: { fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' },
    outcomesList: { listStyle: 'none', padding: 0, margin: 0 },
    outcomeItem: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '6px' },
    enrollBtn: { width: '100%', padding: '16px 24px', borderRadius: '12px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', border: 'none', textTransform: 'uppercase', letterSpacing: '0.5px' },
    aiEngineerBtn: { background: 'linear-gradient(135deg, #4f7cff 0%, #3d63cc 100%)', color: 'white', boxShadow: '0 4px 20px rgba(79, 124, 255, 0.3)' },
    dataScienceBtn: { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', boxShadow: '0 4px 20px rgba(34, 197, 94, 0.3)' },
    leadershipBtn: { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)' }
  };

  const workshopTypesSectionStyles = {
    contentWrapper: { maxWidth: '1200px', margin: '0 auto' },
    header: { textAlign: 'center', marginBottom: isMobile ? '50px' : '80px' },
    badge: { backgroundColor: 'rgba(79, 124, 255, 0.15)', border: '1px solid rgba(79, 124, 255, 0.4)', borderRadius: '25px', padding: '8px 20px', fontSize: '0.9rem', fontWeight: 500, marginBottom: '24px', backdropFilter: 'blur(15px)', color: '#87ceeb', display: 'inline-block' },
    mainTitle: { fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '16px', color: 'white' },
    subtitle: { fontSize: isMobile ? '1.05rem' : '1.2rem', color: 'rgba(255, 255, 255, 0.8)', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6 },
    workshopsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px', marginBottom: '60px' }, // auto-fit works well here for mobile
    workshopCard: { backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '20px', padding: isMobile ? '32px 24px' : '40px 32px', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', textAlign: 'center' },
    cardGlow: { position: 'absolute', top: 0, left: 0, right: 0, height: '3px', borderRadius: '20px 20px 0 0' },
    machineLearningGlow: { background: 'linear-gradient(90deg, #8b5cf6, #7c3aed)' },
    aiDevelopmentGlow: { background: 'linear-gradient(90deg, #3b82f6, #2563eb)' },
    dataScienceGlow: { background: 'linear-gradient(90deg, #10b981, #059669)' },
    generativeAiGlow: { background: 'linear-gradient(90deg, #f59e0b, #d97706)' },
    cardIcon: { width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', margin: '0 auto 24px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' },
    machineLearningIcon: { backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6', border: '2px solid rgba(139, 92, 246, 0.3)' },
    aiDevelopmentIcon: { backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', border: '2px solid rgba(59, 130, 246, 0.3)' },
    dataScienceIcon: { backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10b981', border: '2px solid rgba(16, 185, 129, 0.3)' },
    generativeAiIcon: { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', border: '2px solid rgba(245, 158, 11, 0.3)' },
    cardTitle: { fontSize: '1.4rem', fontWeight: 700, color: 'white', marginBottom: '16px', lineHeight: 1.3 },
    cardDescription: { fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, marginBottom: '24px', minHeight: '80px' },
    topicsContainer: { marginBottom: '24px' },
    topicsTitle: { fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px', textAlign: 'left' },
    topicsList: { textAlign: 'left' },
    topicItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px' },
    checkIcon: { color: '#10b981', fontSize: '12px', fontWeight: 'bold' },
    highlightsSection: { marginTop: '80px' },
    highlightsTitle: { fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 700, textAlign: 'center', marginBottom: '16px', color: 'white' },
    highlightsSubtitle: { fontSize: isMobile ? '1rem' : '1.1rem', color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', marginBottom: '60px', maxWidth: '600px', margin: '0 auto 60px' },
    highlightsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' },
    highlightCard: { backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '20px', padding: '32px', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', textAlign: 'center' },
    highlightIcon: { width: '60px', height: '60px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 20px', backgroundColor: 'rgba(79, 124, 255, 0.2)', color: '#4f7cff' },
    highlightTitle: { fontSize: '1.2rem', fontWeight: 700, color: 'white', marginBottom: '12px' },
    highlightDescription: { fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }
  };
  
  const contactFormStyles = {
    contentWrapper: { maxWidth: '600px', width: '100%'},
    formCard: { backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '24px', padding: isMobile ? '32px 24px' : '48px 40px', boxShadow: '0 25px 70px rgba(0, 0, 0, 0.3)' },
    header: { textAlign: 'center', marginBottom: '40px' },
    badge: { backgroundColor: 'rgba(79, 124, 255, 0.15)', border: '1px solid rgba(79, 124, 255, 0.4)', borderRadius: '25px', padding: '8px 20px', fontSize: '0.9rem', fontWeight: 500, marginBottom: '24px', backdropFilter: 'blur(15px)', color: '#87ceeb', display: 'inline-block' },
    mainTitle: { fontSize: isMobile ? '2rem' : '2.5rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '16px', color: 'white' },
    subtitle: { fontSize: isMobile ? '1rem' : '1.1rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6, marginBottom: '20px' },
    buildTitle: { fontSize: '1.3rem', fontWeight: 700, color: 'white', marginBottom: '32px', textAlign: 'center' },
    formRow: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', marginBottom: isMobile ? 0 : '24px' },
    formGroup: { flex: 1, marginBottom: '24px' },
    label: { display: 'block', fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' },
    input: { width: '100%', padding: '14px 16px', backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '12px', fontSize: '1rem', color: 'white', outline: 'none', transition: 'all 0.3s ease', backdropFilter: 'blur(10px)', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '14px 16px', backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '12px', fontSize: '1rem', color: 'white', outline: 'none', transition: 'all 0.3s ease', backdropFilter: 'blur(10px)', resize: 'vertical', minHeight: '120px', fontFamily: 'inherit', boxSizing: 'border-box' },
    submitBtn: { width: '100%', padding: '16px 24px', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)', backdropFilter: 'blur(10px)', marginTop: '8px' },
    responseText: { fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', marginTop: '20px', lineHeight: 1.5 },
  };

  //================================================================================
  //== DATA AND STATE
  //================================================================================

  const workshops = [
    { level: 'Beginner', title: 'Machine Learning Fundamentals', description: 'Master the core concepts of ML algorithms, data preprocessing, and model evaluation. Perfect for beginners.', duration: '8 weeks', students: '2,800+', rating: '4.9', topics: ['Python & Scikit-learn', 'Real datasets', 'Portfolio projects', 'Certification'], type: 'beginner' },
    { level: 'Intermediate', title: 'Deep Learning & Neural Networks', description: 'Dive deep into neural networks, CNNs, RNNs, and transformers. Build cutting-edge AI applications from scratch.', duration: '12 weeks', students: '1,800+', rating: '4.8', topics: ['TensorFlow & PyTorch', 'Computer vision', 'NLP projects', 'GPU training'], type: 'intermediate' },
    { level: 'All Levels', title: 'AI Ethics & Responsible AI', description: 'Understand the ethical implications of AI, bias detection, fairness, and building responsible AI systems.', duration: '4 weeks', students: '950+', rating: '4.9', topics: ['Bias detection', 'Fairness metrics', 'Legal frameworks', 'Case studies'], type: 'allLevels' },
    { level: 'Advanced', title: 'Generative AI & LLMs', description: 'Explore the world of GPT, ChatGPT, and generative models. Learn prompt engineering and fine-tuning techniques.', duration: '6 weeks', students: '3,200+', rating: '4.9', topics: ['Prompt engineering', 'Fine-tuning', 'API integration', 'Custom models'], type: 'advanced' },
    { level: 'Executive', title: 'AI for Business Leaders', description: 'Strategic AI implementation for executives and managers. No coding required - focus on strategy and ROI.', duration: '3 weeks', students: '1,800+', rating: '4.7', topics: ['AI strategy', 'ROI calculation', 'Team building', 'Vendor selection'], type: 'executive' }
  ];

  const bootcamps = [
    { type: 'aiEngineer', title: 'AI Engineer Bootcamp', subtitle: 'Full-Stack AI Development', description: 'Intensive 16-week program covering everything from ML fundamentals to production deployment. Build 5+ real-world AI applications.', duration: '16 weeks', format: 'Hybrid', startDate: 'March 15, 2024', spotsLeft: '12 spots left', includes: ['1-on-1 mentorship', 'Job placement guarantee', 'Portfolio development', 'Industry partnerships', 'Lifetime access', 'Weekly code reviews'], outcomes: ['Build production-ready AI apps', 'Master MLOps & deployment', 'Land $100k+ AI engineer role', 'Create impressive portfolio'], icon: '🤖', popular: false },
    { type: 'dataScience', title: 'Data Science Intensive', subtitle: 'Analytics to AI Pipeline', description: 'Transform from data analyst to AI practitioner in 12 weeks. Focus on business applications and real-world problem solving.', duration: '12 weeks', format: 'Online', startDate: 'April 1, 2024', spotsLeft: '8 spots left', includes: ['Business case studies', 'Industry datasets', 'Capstone project', 'Networking events', 'Certification included', 'Post-program consulting'], outcomes: ['Master advanced analytics', 'Build predictive models', 'Transition to AI role', 'Increase salary 50%+'], icon: '📊', popular: true },
    { type: 'leadership', title: 'AI Leadership Program', subtitle: 'Executive AI Strategy', description: '6-week intensive for executives and senior managers. Learn to lead AI transformation without getting lost in technical details.', duration: '6 weeks', format: 'In-person', startDate: 'March 22, 2024', spotsLeft: '5 spots left', includes: ['C-level instruction', 'Strategy frameworks', 'Team building guidance', 'Vendor evaluation tools', 'ROI calculation tools', 'Post-program consulting'], outcomes: ['Develop AI strategy', 'Build high-performing teams', 'Maximize ROI on investments', 'Lead digital transformation'], icon: '👨‍💼', popular: false }
  ];
  
  const workshopTypes = [
    { type: 'dataScience', title: 'Data Science', description: 'Master data analysis, visualization, and predictive modeling for business insights', topics: ['Data analysis & visualization', 'Predictive modeling', 'Business intelligence', 'Statistical methods'], icon: '📊' },
    { type: 'generativeAi', title: 'Generative AI', description: 'Explore LLMs, prompt engineering, and generative models for creative applications', topics: ['Large Language Models', 'Prompt engineering', 'Creative AI applications', 'Model fine-tuning'], icon: '✨' },
    { type: 'aiBusinessStrategy', title: 'AI Business Strategy', description: 'Develop strategic roadmaps for integrating AI into your organization to drive growth and innovation.', topics: ['AI integration roadmaps', 'Leading AI transformation', 'Measuring AI ROI', 'Competitive advantage'], icon: '📈' },
    { type: 'automationAi', title: 'Automation & AI', description: 'Implement intelligent automation solutions using AI, machine learning, and robotic process automation (RPA).', topics: ['Robotic Process Automation', 'Intelligent workflows', 'Process mining', 'AI-driven automation'], icon: '🤖' },
    { type: 'mechatronicsAi', title: 'Mechatronics & AI', description: 'Explore the synergy between mechanical engineering, electronics, and AI to build smart robotic systems.', topics: ['Smart robotics', 'Sensor fusion', 'AI in control systems', 'Embedded intelligence'], icon: '🦾' },
    { type: 'iotAi', title: 'IoT & AI', description: 'Harness the power of AI to analyze data from IoT devices for predictive maintenance and smart systems.', topics: ['AI on the edge', 'IoT data analytics', 'Predictive maintenance', 'Smart sensor networks'], icon: '🌐' }
  ];

  const highlights = [
    { title: 'Expert Instructors', description: 'Learn from industry professionals with years of real-world AI experience', icon: '👨‍🏫' },
    { title: 'Certification', description: 'Receive industry-recognized certificates upon successful completion', icon: '🎓' },
    { title: 'Global Community', description: 'Join a worldwide network of AI enthusiasts and professionals', icon: '🌐' },
    { title: 'Flexible Learning', description: 'Choose from various formats: online, hybrid, or in-person sessions', icon: '⏰' },
    { title: 'Hands-on Projects', description: 'Apply your knowledge by working on real-world case studies and building a professional portfolio', icon: '🛠️' },
    { title: 'Career Support', description: 'Receive guidance with resume building, interview preparation, and access to our exclusive job network', icon: '💼' }
  ];
  
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  //================================================================================
  //== EVENT HANDLERS
  //================================================================================
  
  const handleCardHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = '0 20px 60px rgba(255, 20, 147, 0.2)';
    e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.2)';
  };

  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.1)';
  };
  
  const handleWorkshopBtnHover = (e, type) => {
    e.target.style.transform = 'translateY(-2px)';
    const shadowMap = {
        beginner: '0 8px 30px rgba(79, 124, 255, 0.5)', intermediate: '0 8px 30px rgba(34, 197, 94, 0.5)',
        allLevels: '0 8px 30px rgba(245, 158, 11, 0.5)', advanced: '0 8px 30px rgba(139, 92, 246, 0.5)',
        executive: '0 8px 30px rgba(239, 68, 68, 0.5)',
    };
    e.target.style.boxShadow = shadowMap[type] || '';
  };
  
  const handleWorkshopBtnLeave = (e, type) => {
      e.target.style.transform = 'translateY(0)';
      const shadowMap = {
          beginner: '0 4px 20px rgba(79, 124, 255, 0.3)', intermediate: '0 4px 20px rgba(34, 197, 94, 0.3)',
          allLevels: '0 4px 20px rgba(245, 158, 11, 0.3)', advanced: '0 4px 20px rgba(139, 92, 246, 0.3)',
          executive: '0 4px 20px rgba(239, 68, 68, 0.3)',
      };
      e.target.style.boxShadow = shadowMap[type] || '';
  };

  const getWorkshopStylesByType = (type) => {
    switch(type) {
      case 'beginner': return { badge: workshopSectionStyles.beginnerBadge, glow: workshopSectionStyles.beginnerGlow, btn: workshopSectionStyles.beginnerBtn };
      case 'intermediate': return { badge: workshopSectionStyles.intermediateBadge, glow: workshopSectionStyles.intermediateGlow, btn: workshopSectionStyles.intermediateBtn };
      case 'allLevels': return { badge: workshopSectionStyles.allLevelsBadge, glow: workshopSectionStyles.allLevelsGlow, btn: workshopSectionStyles.allLevelsBtn };
      case 'advanced': return { badge: workshopSectionStyles.advancedBadge, glow: workshopSectionStyles.advancedGlow, btn: workshopSectionStyles.advancedBtn };
      case 'executive': return { badge: workshopSectionStyles.executiveBadge, glow: workshopSectionStyles.executiveGlow, btn: workshopSectionStyles.executiveBtn };
      default: return { badge: workshopSectionStyles.beginnerBadge, glow: workshopSectionStyles.beginnerGlow, btn: workshopSectionStyles.beginnerBtn };
    }
  };

  const handleBootcampBtnHover = (e, type) => {
    e.target.style.transform = 'translateY(-2px)';
    const shadowMap = {
        aiEngineer: '0 8px 30px rgba(79, 124, 255, 0.5)', dataScience: '0 8px 30px rgba(34, 197, 94, 0.5)',
        leadership: '0 8px 30px rgba(245, 158, 11, 0.5)',
    };
    e.target.style.boxShadow = shadowMap[type] || '';
  };
  
  const handleBootcampBtnLeave = (e, type) => {
    e.target.style.transform = 'translateY(0)';
    const shadowMap = {
        aiEngineer: '0 4px 20px rgba(79, 124, 255, 0.3)', dataScience: '0 4px 20px rgba(34, 197, 94, 0.3)',
        leadership: '0 4px 20px rgba(245, 158, 11, 0.3)',
    };
    e.target.style.boxShadow = shadowMap[type] || '';
  };

  const getBootcampStylesByType = (type) => {
    switch(type) {
      case 'aiEngineer': return { glow: bootcampSectionStyles.aiEngineerGlow, icon: bootcampSectionStyles.aiEngineerIcon, btn: bootcampSectionStyles.aiEngineerBtn };
      case 'dataScience': return { glow: bootcampSectionStyles.dataScienceGlow, icon: bootcampSectionStyles.dataScienceIcon, btn: bootcampSectionStyles.dataScienceBtn };
      case 'leadership': return { glow: bootcampSectionStyles.leadershipGlow, icon: bootcampSectionStyles.leadershipIcon, btn: bootcampSectionStyles.leadershipBtn };
      default: return { glow: bootcampSectionStyles.aiEngineerGlow, icon: bootcampSectionStyles.aiEngineerIcon, btn: bootcampSectionStyles.aiEngineerBtn };
    }
  };

  const getWorkshopTypesStylesByType = (type) => {
    switch(type) {
      case 'machineLearning': return { glow: workshopTypesSectionStyles.machineLearningGlow, icon: workshopTypesSectionStyles.machineLearningIcon };
      case 'aiDevelopment': return { glow: workshopTypesSectionStyles.aiDevelopmentGlow, icon: workshopTypesSectionStyles.aiDevelopmentIcon };
      case 'dataScience': return { glow: workshopTypesSectionStyles.dataScienceGlow, icon: workshopTypesSectionStyles.dataScienceIcon };
      case 'generativeAi': return { glow: workshopTypesSectionStyles.generativeAiGlow, icon: workshopTypesSectionStyles.generativeAiIcon };
      default: return { glow: workshopTypesSectionStyles.machineLearningGlow, icon: workshopTypesSectionStyles.machineLearningIcon };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New Workshop Inquiry
      --------------------

      Inquirer Details:
      - Name: ${formData.firstName} ${formData.lastName}
      - Email: ${formData.email}
      - Phone: ${formData.phone || 'Not provided'}

      Message:
      ${formData.message}
    `;

    const templateParams = {
        email_subject: "New Workshop Inquiry",
        email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your message! We will get back to you soon.');
        setIsSubmitting(false);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('An error occurred while sending your message. Please try again later.');
        setIsSubmitting(false);
      });
  };
  
  const handleInputFocus = (e) => {
    e.target.style.border = '1px solid rgba(99, 102, 241, 0.5)';
    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
  };
  
  const handleInputBlur = (e) => {
    e.target.style.border = '1px solid rgba(255, 255, 255, 0.2)';
    e.target.style.boxShadow = 'none';
  };
  
  const handleContactBtnHover = (e) => {
    if (e.target.disabled) return;
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 12px 40px rgba(99, 102, 241, 0.4)';
  };
  
  const handleContactBtnLeave = (e) => {
    if (e.target.disabled) return;
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 8px 32px rgba(99, 102, 241, 0.3)';
  };
    
  return (
    <div style={mainStyles.appContainer}>
      <div style={mainStyles.backgroundOverlay}></div>
      <div style={mainStyles.networkMesh}></div>

      {/* Section 1: Workshop Selection */}
      <div style={mainStyles.sectionWrapper}>
        <div style={workshopSectionStyles.contentWrapper}>
          <header style={workshopSectionStyles.header}>
            <div style={workshopSectionStyles.badge}>Workshops</div>
            <h1 style={workshopSectionStyles.mainTitle}>Choose Your Learning Path</h1>
            <p style={workshopSectionStyles.subtitle}>From fundamentals to advanced applications, our workshops are designed to accelerate your AI journey</p>
          </header>
          <div style={workshopSectionStyles.workshopsGrid}>
            {workshops.slice(0, 3).map((workshop, index) => {
              const typeStyles = getWorkshopStylesByType(workshop.type);
              return (
                <article key={index} style={workshopSectionStyles.workshopCard} onMouseEnter={handleCardHover} onMouseLeave={handleCardLeave}>
                  <div style={{ ...workshopSectionStyles.cardGlow, ...typeStyles.glow }}></div>
                  <h3 style={workshopSectionStyles.cardTitle}>{workshop.title}</h3>
                  <p style={workshopSectionStyles.cardDescription}>{workshop.description}</p>
                  <div style={workshopSectionStyles.cardMeta}>
                    <div style={workshopSectionStyles.metaItem}><span>📅</span><span>{workshop.duration}</span></div>
                  </div>
                  <div style={workshopSectionStyles.topicsContainer}>
                    <div style={workshopSectionStyles.topicsTitle}>Key Topics:</div>
                    <div style={workshopSectionStyles.topicsList}>
                      {workshop.topics.map((topic, i) => <span key={i} style={workshopSectionStyles.topicChip}>{topic}</span>)}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <div style={workshopSectionStyles.bottomRow}>
            {workshops.slice(3).map((workshop, index) => {
              const typeStyles = getWorkshopStylesByType(workshop.type);
              return (
                <article key={index + 3} style={workshopSectionStyles.workshopCard} onMouseEnter={handleCardHover} onMouseLeave={handleCardLeave}>
                  <div style={{ ...workshopSectionStyles.cardGlow, ...typeStyles.glow }}></div>
                  <div style={workshopSectionStyles.cardHeader}>
                      <div style={{ ...workshopSectionStyles.levelBadge, ...typeStyles.badge }}>{workshop.level}</div>
                      {workshop.price && <div style={workshopSectionStyles.price}>{workshop.price}</div>}
                  </div>
                  <h3 style={workshopSectionStyles.cardTitle}>{workshop.title}</h3>
                  <p style={workshopSectionStyles.cardDescription}>{workshop.description}</p>
                  <div style={workshopSectionStyles.cardMeta}>
                    <div style={workshopSectionStyles.metaItem}><span>📅</span><span>{workshop.duration}</span></div>
                  </div>
                  <div style={workshopSectionStyles.topicsContainer}>
                    <div style={workshopSectionStyles.topicsTitle}>Key Topics:</div>
                    <div style={workshopSectionStyles.topicsList}>
                      {workshop.topics.map((topic, i) => <span key={i} style={workshopSectionStyles.topicChip}>{topic}</span>)}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
      
      <hr style={{borderColor: 'rgba(255, 255, 255, 0.1)'}} />

      {/* Section 2: Bootcamp Selection */}
      <div style={mainStyles.sectionWrapper}>
        <div style={bootcampSectionStyles.contentWrapper}>
          <header style={bootcampSectionStyles.header}>
            <div style={bootcampSectionStyles.badge}>Intensive Bootcamps</div>
            <h1 style={bootcampSectionStyles.mainTitle}>Accelerate Your Career</h1>
            <p style={bootcampSectionStyles.subtitle}>Immersive programs designed to transform your career in months, not years. With job guarantees and lifetime support.</p>
          </header>
          <div style={bootcampSectionStyles.bootcampsGrid}>
            {bootcamps.map((bootcamp, index) => {
              const typeStyles = getBootcampStylesByType(bootcamp.type);
              return (
                <article key={index} style={bootcampSectionStyles.bootcampCard} onMouseEnter={handleCardHover} onMouseLeave={handleCardLeave}>
                  <div style={{...bootcampSectionStyles.cardGlow, ...typeStyles.glow}}></div>
                  <div style={bootcampSectionStyles.cardHeader}>
                    <div><div style={{...bootcampSectionStyles.cardIcon, ...typeStyles.icon}}>{bootcamp.icon}</div></div>
                    {bootcamp.popular && <div style={bootcampSectionStyles.popularBadge}>Most Popular</div>}
                  </div>
                  <h3 style={bootcampSectionStyles.cardTitle}>{bootcamp.title}</h3>
                  <p style={bootcampSectionStyles.cardSubtitle}>{bootcamp.subtitle}</p>
                  <p style={bootcampSectionStyles.cardDescription}>{bootcamp.description}</p>
                  <div style={bootcampSectionStyles.cardMeta}>
                    <div style={bootcampSectionStyles.metaItem}><span style={bootcampSectionStyles.metaIcon}>📅</span><span>{bootcamp.duration}</span></div>
                    <div style={bootcampSectionStyles.metaItem}><span style={bootcampSectionStyles.metaIcon}>💻</span><span>{bootcamp.format}</span></div>
                  </div>
                  <div style={bootcampSectionStyles.includesSection}>
                    <div style={bootcampSectionStyles.includesTitle}>What's Included:</div>
                    <div style={bootcampSectionStyles.includesList}>{bootcamp.includes.map((item, i) => <div key={i} style={bootcampSectionStyles.includeItem}><span style={bootcampSectionStyles.checkIcon}>✓</span><span>{item}</span></div>)}</div>
                  </div>
                  <div style={bootcampSectionStyles.outcomesSection}>
                    <div style={bootcampSectionStyles.outcomesTitle}>Career Outcomes:</div>
                    <ul style={bootcampSectionStyles.outcomesList}>{bootcamp.outcomes.map((outcome, i) => <li key={i} style={bootcampSectionStyles.outcomeItem}><span style={bootcampSectionStyles.checkIcon}>○</span><span>{outcome}</span></li>)}</ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>

      <hr style={{borderColor: 'rgba(255, 255, 255, 0.1)'}} />

      {/* Section 3: Workshop Types */}
      <div style={mainStyles.sectionWrapper}>
        <div style={workshopTypesSectionStyles.contentWrapper}>
          <header style={workshopTypesSectionStyles.header}>
            <div style={workshopTypesSectionStyles.badge}>Our Expertise</div>
            <h1 style={workshopTypesSectionStyles.mainTitle}>What Kind of Workshops We Conduct</h1>
            <p style={workshopTypesSectionStyles.subtitle}>Comprehensive AI education covering every aspect of artificial intelligence and machine learning</p>
          </header>
          <div style={workshopTypesSectionStyles.workshopsGrid}>
            {workshopTypes.map((workshop, index) => {
              const typeStyles = getWorkshopTypesStylesByType(workshop.type);
              return (
                <article key={index} style={workshopTypesSectionStyles.workshopCard} onMouseEnter={handleCardHover} onMouseLeave={handleCardLeave}>
                  <div style={{...workshopTypesSectionStyles.cardGlow, ...typeStyles.glow}}></div>
                  <div style={{...workshopTypesSectionStyles.cardIcon, ...typeStyles.icon}}>{workshop.icon}</div>
                  <h3 style={workshopTypesSectionStyles.cardTitle}>{workshop.title}</h3>
                  <p style={workshopTypesSectionStyles.cardDescription}>{workshop.description}</p>
                  <div style={workshopTypesSectionStyles.topicsContainer}>
                    <div style={workshopTypesSectionStyles.topicsTitle}>Key Areas:</div>
                    <div style={workshopTypesSectionStyles.topicsList}>{workshop.topics.map((topic, i) => <div key={i} style={workshopTypesSectionStyles.topicItem}><span style={workshopTypesSectionStyles.checkIcon}>✓</span><span>{topic}</span></div>)}</div>
                  </div>
                </article>
              );
            })}
          </div>
          <div style={workshopTypesSectionStyles.highlightsSection}>
            <header>
              <div style={workshopTypesSectionStyles.badge}>Why Choose Us</div>
              <h2 style={workshopTypesSectionStyles.highlightsTitle}>Workshop Highlights</h2>
              <p style={workshopTypesSectionStyles.highlightsSubtitle}>Experience world-class AI education with unmatched quality and support</p>
            </header>
            <div style={workshopTypesSectionStyles.highlightsGrid}>
              {highlights.map((highlight, index) => (
                <article key={index} style={workshopTypesSectionStyles.highlightCard} onMouseEnter={handleCardHover} onMouseLeave={handleCardLeave}>
                  <div style={workshopTypesSectionStyles.highlightIcon}>{highlight.icon}</div>
                  <h3 style={workshopTypesSectionStyles.highlightTitle}>{highlight.title}</h3>
                  <p style={workshopTypesSectionStyles.highlightDescription}>{highlight.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      <hr style={{borderColor: 'rgba(255, 255, 255, 0.1)'}} />

      {/* Section 4: Contact Form */}
      <div style={mainStyles.contactSectionWrapper}>
        <div style={contactFormStyles.contentWrapper}>
          <div style={contactFormStyles.formCard}>
            <header style={contactFormStyles.header}>
              <div style={contactFormStyles.badge}>Get in Touch</div>
              <h1 style={contactFormStyles.mainTitle}>Connect With Us</h1>
              <p style={contactFormStyles.subtitle}>Ready to start your AI journey? Get in touch with our team for personalized guidance</p>
            </header>
            <form onSubmit={handleSubmit}>
              <h2 style={contactFormStyles.buildTitle}>Let's Build Your AI Future Together</h2>
              <div style={contactFormStyles.formRow}>
                <div style={{...contactFormStyles.formGroup, marginBottom: isMobile ? '24px' : 0}}>
                    <label htmlFor="firstName" style={contactFormStyles.label}>First Name</label>
                    <input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} style={contactFormStyles.input} placeholder="John" required />
                </div>
                <div style={{...contactFormStyles.formGroup, marginBottom: isMobile ? '24px' : 0}}>
                    <label htmlFor="lastName" style={contactFormStyles.label}>Last Name</label>
                    <input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} style={contactFormStyles.input} placeholder="Doe" required />
                </div>
              </div>
              <div style={contactFormStyles.formGroup}><label htmlFor="email" style={contactFormStyles.label}>Email</label><input id="email" type="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} style={contactFormStyles.input} placeholder="john.doe@example.com" required /></div>
              <div style={contactFormStyles.formGroup}><label htmlFor="phone" style={contactFormStyles.label}>Phone (Optional)</label><input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} style={contactFormStyles.input} placeholder="+1 (555) 123-4567" /></div>
              <div style={contactFormStyles.formGroup}><label htmlFor="message" style={contactFormStyles.label}>Message</label><textarea id="message" name="message" value={formData.message} onChange={handleInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} style={contactFormStyles.textarea} placeholder="Tell us about your AI learning goals..." required /></div>
              <button type="submit" style={contactFormStyles.submitBtn} onMouseEnter={handleContactBtnHover} onMouseLeave={handleContactBtnLeave} disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              <p style={contactFormStyles.responseText}>We'll get back to you within 24 hours to discuss your AI learning journey</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWorkshopLanding1;
